import { buildApiUrl, getAuthHeaders, getDefaultHeaders, API_ENDPOINTS } from '../config/api';
import { goToLogin } from '../utils/navigation';

export interface LoginData {
  phone: string;
  password: string;
}

export interface SignupData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  password: string;
  role: string;
}

export interface ResendOTPResponse {
  message: string;
  userId: number;
  verificationRequired: boolean;
  expiresAt: string;
  viaSMS: boolean;
  viaEmail: boolean;
}

export interface AdminUserData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  role: 'COMMITTEE_ADMIN' | 'COMMITTEE' | 'VENDOR_ADMIN' | 'VENDOR' | 'DEVOTEE';
  password?: string;
}

export interface AdminUser {
  USER_ID: number;
  FIRST_NAME?: string;
  LAST_NAME?: string;
  PHONE: string;
  EMAIL: string;
  ROLE: string;
  CREATED_AT: string;
  createdBy: number;
}

export interface ContactData {
  fullName: string;
  email: string;
  subject: string;
  message: string;
  phone: string;
}

export interface EventOrganizer {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface EventMedia {
  id: number;
  url: string;
  caption: string;
  type: string;
  isFeatured: boolean;
}

export interface EventThumbnail {
  base64: string;
  mimeType: string;
}


// Actual API response structure
export interface EventDetail {
  id: number;
  title: string;
  description: string;
  location: string;
  latitude: string; // API returns string, not number
  longitude: string; // API returns string, not number
  startDate: string;
  endDate: string;
  expectedAttendance: number;
  isVerified: boolean;
  isActive: boolean;
  createdBy: number;
  createdByRole: string;
  verifiedBy?: number;
  createdAt: string;
  updatedAt: string;
  isFeatured: boolean;
  featuredAt?: string;
  thumbnailBase64?: string;
  thumbnailMimeType?: string;
  // Optional fields that may not be in the API
  address?: string;
  contactInfo?: string;
  website?: string;
  category?: string;
  tags?: string[];
  verifiedAt?: string;
  organizer?: EventOrganizer;
  media?: EventMedia[];
  thumbnail?: EventThumbnail;
  imageUrl?: string;
}

export interface ApiResponse<T = any> {
  success?: boolean;
  message?: string;
  data?: T;
  event?: EventDetail;
  token?: string;
  userId?: number;
  role?: string;
  firstName?: string;
  users?: T[];
  user?: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = buildApiUrl(endpoint);
    
    const config: RequestInit = {
      headers: {
        ...getDefaultHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log(`🔄 API Request: ${config.method || 'GET'} ${url}`);
      const response = await fetch(url, config);
      
      // Log response status
      console.log(`📡 API Response: ${response.status} ${response.statusText}`);
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error(`Invalid JSON response from server (${response.status})`);
      }

      if (!response.ok) {
        // Global auth interceptor: on 401/403, clear session and navigate to login
        if (response.status === 401 || response.status === 403) {
          try {
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
          } catch (_) {}
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            goToLogin();
          }
        }
        console.error(`❌ API Error:`, data);
        throw new Error(data.message || `Server error: ${response.status} ${response.statusText}`);
      }

      console.log(`✅ API Success:`, data);
      return data;
    } catch (error) {
      console.error(`🚨 API Request Failed:`, error);
      throw error;
    }
  }

  public async get<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  return this.request<T>(endpoint, {
    method: 'GET',
    ...options,
  });
}

  // Authentication methods
  async login(credentials: LoginData): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async signup(userData: SignupData): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async requestOTP(phone: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.REQUEST_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  async verifyOTP(phone: string, otp: string, loginMethod: 'otp' | 'registration' | 'password' = 'otp'): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone, otp, loginMethod }),
    });
  }

  async resendOTP(phone: string): Promise<ApiResponse<ResendOTPResponse>> {
    return this.request<ResendOTPResponse>(API_ENDPOINTS.AUTH.RESEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
  }

  async forgotPassword(email?: string, phone?: string): Promise<ApiResponse> {
    const body: { email?: string; phone?: string } = {};
    if (email) body.email = email;
    if (phone) body.phone = phone;
    
    return this.request(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async verifyResetOTP(phone: string, otpCode: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.VERIFY_RESET_OTP, {
      method: 'POST',
      body: JSON.stringify({ phone, otpCode }),
    });
  }

  async resetPassword(phone: string, newPassword: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ phone, newPassword }),
    });
  }

  // Admin User Management methods (based on backend documentation)
  async createAdminUser(userData: AdminUserData, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.ADMIN_USERS.BASE, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(userData),
    });
  }

  async getAdminUsers(page: number = 1, limit: number = 10, token: string): Promise<ApiResponse> {
    return this.request(`${API_ENDPOINTS.ADMIN_USERS.BASE}?page=${page}&limit=${limit}`, {
      headers: getAuthHeaders(token),
    });
  }

  async getAdminUserById(userId: number, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.ADMIN_USERS.BY_ID(userId), {
      headers: getAuthHeaders(token),
    });
  }

  async updateAdminUser(userId: number, userData: Partial<AdminUserData>, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.ADMIN_USERS.BY_ID(userId), {
      method: 'PUT',
      headers: getAuthHeaders(token),
      body: JSON.stringify(userData),
    });
  }

  // Legacy admin methods (keeping for backwards compatibility)
  async createUser(userData: SignupData, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.USERS.ADMIN_USERS, {
      method: 'POST',
      headers: getAuthHeaders(token),
      body: JSON.stringify(userData),
    });
  }

  async getUsers(token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.USERS.ADMIN_USERS, {
      headers: getAuthHeaders(token),
    });
  }

  // User Profile methods
  async getUserProfile(userId: number, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.USERS.PROFILE(userId), {
      headers: getAuthHeaders(token),
    });
  }

  // Event management methods (based on backend documentation)
  
  // Public event endpoints
  async getPublicEvents(params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<ApiResponse> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.PUBLIC}?${queryString}` : API_ENDPOINTS.EVENTS.PUBLIC;
    
    return this.request(endpoint);
  }

  async getAllEvents(token: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<ApiResponse> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.ALL}?${queryString}` : API_ENDPOINTS.EVENTS.ALL;
    
    return this.request(endpoint, {
      headers: getAuthHeaders(token),
    });
  }

  async getUpcomingEvents(limit: number = 10): Promise<ApiResponse> {
    return this.request(`${API_ENDPOINTS.EVENTS.UPCOMING}?limit=${limit}`);
  }

  async getFeaturedEvents(limit: number = 10): Promise<ApiResponse> {
    return this.request(`${API_ENDPOINTS.EVENTS.FEATURED}?limit=${limit}`);
  }

  async getTrendingEvents(params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<ApiResponse> {
    // Build query string from parameters (same as getPublicEvents)
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.TRENDING}?${queryString}` : API_ENDPOINTS.EVENTS.TRENDING;
    
    return this.request(endpoint);
  }

  async searchEvents(query: string, params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    category?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams({ search: query });
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    
    return this.request(`${API_ENDPOINTS.EVENTS.SEARCH}?${queryParams.toString()}`);
  }

  async getEventsByLocation(location: string, params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams({ location });
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    return this.request(`${API_ENDPOINTS.EVENTS.BY_LOCATION}?${queryParams.toString()}`);
  }

  async getEventsByDateRange(startDate: string, endDate: string, params?: {
    page?: number;
    limit?: number;
    category?: string;
    isFeatured?: boolean;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams({ startDate, endDate });
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    
    return this.request(`${API_ENDPOINTS.EVENTS.BY_DATE_RANGE}?${queryParams.toString()}`);
  }

  async getEventById(eventId: number, token?: string): Promise<ApiResponse<EventDetail>> {
    console.log('🎯 API Service: Getting event by ID:', eventId);
    console.log('🔐 API Service: Token provided:', token ? 'Yes' : 'No');
    
    const options: RequestInit = {};
    
    // Add authorization header if token is provided
    if (token) {
      options.headers = getAuthHeaders(token);
      console.log('🔑 API Service: Added auth headers');
    }
    
    const endpoint = API_ENDPOINTS.EVENTS.BY_ID(eventId);
    console.log('📍 API Service: Endpoint:', endpoint);
    
    const result = await this.request<EventDetail>(endpoint, options);
    console.log('🎪 API Service: Raw result from request:', result);
    
    return result;
  }

  // Authenticated event endpoints
  async getEvents(token: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    status?: string;
  }): Promise<ApiResponse> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.PUBLIC}?${queryString}` : API_ENDPOINTS.EVENTS.PUBLIC;
    
    return this.request(endpoint, {
      headers: getAuthHeaders(token),
    });
  }

  async createEvent(eventData: FormData | any, token: string): Promise<ApiResponse> {
    // Check if eventData is FormData (contains files) or regular object
    const isFormData = eventData instanceof FormData;
    
    const headers = isFormData 
      ? { 'Authorization': `Bearer ${token}` } // Don't set Content-Type for FormData
      : getAuthHeaders(token);

    return this.request(API_ENDPOINTS.EVENTS.BASE, {
      method: 'POST',
      headers,
      body: isFormData ? eventData : JSON.stringify(eventData),
    });
  }

  async createWithUser(data: FormData, token?: string): Promise<ApiResponse> {
    const headers = token ? getAuthHeaders(token) : undefined;
    // Remove Content-Type header to let browser set it with boundary for FormData
    if (headers) {
      delete headers['Content-Type'];
    }
    return this.request(API_ENDPOINTS.EVENTS.CREATE_WITH_USER, {
      method: 'POST',
      headers,
      body: data,
    });
  }

  async updateEvent(eventId: number, eventData: FormData | any, token: string): Promise<ApiResponse> {
    // Check if eventData is FormData (contains files) or regular object
    const isFormData = eventData instanceof FormData;
    
    const headers = isFormData 
      ? { 'Authorization': `Bearer ${token}` } // Don't set Content-Type for FormData
      : getAuthHeaders(token);

    return this.request(API_ENDPOINTS.EVENTS.BY_ID(eventId), {
      method: 'PUT',
      headers,
      body: isFormData ? eventData : JSON.stringify(eventData),
    });
  }

  async deleteEvent(eventId: number, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.BY_ID(eventId), {
      method: 'DELETE',
      headers: getAuthHeaders(token),
    });
  }

  // Admin-specific event endpoints
  async getAdminEvents(token: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    status?: string;
  }): Promise<ApiResponse> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.ADMIN}?${queryString}` : API_ENDPOINTS.EVENTS.ADMIN;
    
    return this.request(endpoint, {
      headers: getAuthHeaders(token),
    });
  }

  async getMyEvents(token: string, params?: {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    category?: string;
    isFeatured?: boolean;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  }): Promise<ApiResponse> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isFeatured !== undefined) queryParams.append('isFeatured', params.isFeatured.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.EVENTS.MY_EVENTS}?${queryString}` : API_ENDPOINTS.EVENTS.MY_EVENTS;
    
    return this.request(endpoint, {
      headers: getAuthHeaders(token),
    });
  }

  async getPendingEvents(token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.PENDING, {
      headers: getAuthHeaders(token),
    });
  }

  async verifyEvent(eventId: number, verificationData: any, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.VERIFY(eventId), {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify(verificationData),
    });
  }

  async bulkVerifyEvents(eventIds: number[], isVerified: boolean, verificationNotes: string, token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.BULK_VERIFY, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify({
        eventIds,
        isVerified,
        verificationNotes,
      }),
    });
  }

  async getEventStats(token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.STATS, {
      headers: getAuthHeaders(token),
    });
  }

  async getDashboardStats(token: string): Promise<ApiResponse> {
    return this.request(API_ENDPOINTS.EVENTS.STATS_DASHBOARD, {
      headers: getAuthHeaders(token),
    });
  }

  async featureEvent(eventId: number, isFeatured: boolean, token: string): Promise<ApiResponse> {
    return this.request(`/events/${eventId}/feature`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ isFeatured }),
    });
  }

  async trendEvent(eventId: number, isTrending: boolean, token: string): Promise<ApiResponse> {
    return this.request(`/events/${eventId}/trending`, {
      method: 'PATCH',
      headers: getAuthHeaders(token),
      body: JSON.stringify({ isTrending }),
    });
  }

  async bulkCreateEvents(csvFile: File, token: string): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('csv', csvFile);

    return this.request(API_ENDPOINTS.EVENTS.BULK_CREATE, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }, // Don't set Content-Type for FormData
      body: formData,
    });
  }

  // Get event thumbnail as byte array
  async getEventThumbnail(eventId: number): Promise<ArrayBuffer> {
    console.log(`🖼️ API Service: Getting thumbnail for event ID: ${eventId}`);
    
    const endpoint = API_ENDPOINTS.EVENTS.THUMBNAIL(eventId);
    const url = buildApiUrl(endpoint);
    
    console.log(`📍 API Service: Thumbnail endpoint: ${url}`);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...getDefaultHeaders(),
        },
      });
      
      console.log(`📡 API Service: Thumbnail response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch thumbnail: ${response.status} ${response.statusText}`);
      }
      
      const responseData = await response.json();
      console.log(`📋 API Service: Thumbnail response data:`, responseData);
      
      // Handle new response structure: { success: true, data: { eventId, base64, size } }
      if (responseData.success && responseData.data && responseData.data.base64) {
        // Convert base64 data URL to ArrayBuffer
        const base64Data = responseData.data.base64;
        const base64String = base64Data.split(',')[1]; // Remove data:image/...;base64, prefix
        const binaryString = atob(base64String);
        const arrayBuffer = new ArrayBuffer(binaryString.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        
        for (let i = 0; i < binaryString.length; i++) {
          uint8Array[i] = binaryString.charCodeAt(i);
        }
        
        console.log(`✅ API Service: Thumbnail converted to ArrayBuffer successfully, size: ${arrayBuffer.byteLength} bytes`);
        return arrayBuffer;
      } else {
        throw new Error('Invalid response structure: missing base64 data');
      }
    } catch (error) {
      console.error(`🚨 API Service: Failed to fetch thumbnail:`, error);
      throw error;
    }
  }

  // Get event thumbnail as data URL (convenience method)
  async getEventThumbnailAsDataUrl(eventId: number): Promise<string> {
    try {
      console.log(`🖼️ API Service: Getting thumbnail data URL for event ID: ${eventId}`);
      
      const endpoint = API_ENDPOINTS.EVENTS.THUMBNAIL(eventId);
      const url = buildApiUrl(endpoint);
      
      console.log(`📍 API Service: Thumbnail endpoint: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...getDefaultHeaders(),
        },
      });
      
      console.log(`📡 API Service: Thumbnail response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch thumbnail: ${response.status} ${response.statusText}`);
      }
      
      const responseData = await response.json();
      console.log(`📋 API Service: Thumbnail response data:`, responseData);
      
      // Handle new response structure: { success: true, data: { eventId, base64, size } }
      if (responseData.success && responseData.data && responseData.data.base64) {
        console.log(`✅ API Service: Thumbnail data URL fetched successfully for event ID: ${eventId}, size: ${responseData.data.size} bytes`);
        return responseData.data.base64;
      } else {
        throw new Error('Invalid response structure: missing base64 data');
      }
    } catch (error) {
      console.error(`🚨 API Service: Failed to fetch thumbnail data URL:`, error);
      throw error;
    }
  }

  // Submit contact form
  async submitContactForm(contactData: ContactData): Promise<{ success: boolean; message: string }> {
    console.log(`📧 API Service: Submitting contact form for: ${contactData.email}`);
    
    const url = buildApiUrl('/contact');
    console.log(`📍 API Service: Contact endpoint: ${url}`);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getDefaultHeaders(),
        },
        body: JSON.stringify(contactData),
      });
      
      console.log(`📡 API Service: Contact response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to submit contact form: ${response.status} ${response.statusText}`);
      }
      
      const responseData = await response.json();
      console.log(`📋 API Service: Contact response data:`, responseData);
      
      return {
        success: true,
        message: responseData.message || 'Thank you for your message! We will get back to you soon.'
      };
    } catch (error) {
      console.error(`🚨 API Service: Failed to submit contact form:`, error);
      throw error;
    }
  }

}



export const apiService = new ApiService(); 